import React, { useState } from 'react';
import styled from 'styled-components';
import { FaPencilAlt, FaDownload } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const downloadFile = (blob, fileName) => {
  const blobURL = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobURL;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(blobURL);
};

const PFSedit = ({ data, onClose }) => {
  const [editable, setEditable] = useState(false);
  const [formData, setFormData] = useState({ ...data });
  const [showDropdown, setShowDropdown] = useState(false);

  const handleSave = () => {
    setEditable(false);
    console.log('Saved data:', formData);
  };

  const handleDownload = (type) => {
    const filename = window.prompt('Enter file name (without extension):', 'project_fund_source');
    if (!filename) return;

    if (type === 'excel') {
      const worksheet = XLSX.utils.json_to_sheet([formData]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'PFS');
      const blob = new Blob(
        [XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })],
        { type: 'application/octet-stream' }
      );
      downloadFile(blob, `${filename}.xlsx`);
    }

    if (type === 'pdf') {
      const doc = new jsPDF();
      doc.autoTable({
        head: [['Field', 'Value']],
        body: Object.entries(formData),
      });
      const blob = doc.output('blob');
      downloadFile(blob, `${filename}.pdf`);
    }

    setShowDropdown(false);
  };

  const fields = [
    { key: 'fundSource', label: 'Fund Source' },
    { key: 'fundYear', label: 'Fund Year' },
    { key: 'fundAmount', label: 'Fund Amount' },
    { key: 'dateStarted', label: 'Date Started', type: 'date' },
    { key: 'dateCompleted', label: 'Date Completed', type: 'date' },
    { key: 'turnoverDate', label: 'Turnover Date', type: 'date' }
  ];

  return (
    <FormPanel>
      <FormHeader>
        <Title>Edit Project Fund Source</Title>
        <Icons>
          <DownloadWrapper>
            <DownloadBtn onClick={() => setShowDropdown((prev) => !prev)} title="Download">
              <FaDownload />
            </DownloadBtn>
            {showDropdown && (
              <DropdownMenu>
                <li onClick={() => handleDownload('excel')}>Download as Excel</li>
                <li onClick={() => handleDownload('pdf')}>Download as PDF</li>
              </DropdownMenu>
            )}
          </DownloadWrapper>
          <CloseBtn onClick={onClose}>✖</CloseBtn>
        </Icons>
      </FormHeader>

      <FormBody>
        {fields.map(({ key, label, type }, i) => (
          <Field key={i}>
            <Label>{label}</Label>
            {editable ? (
              <Input
                type={type || 'text'}
                value={formData[key]}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, [key]: e.target.value }))
                }
              />
            ) : (
              <StaticValue>{formData[key]}</StaticValue>
            )}
          </Field>
        ))}
      </FormBody>

      <FormFooter>
        <EditButton onClick={() => setEditable((prev) => !prev)}>
          <FaPencilAlt /> {editable ? 'Cancel' : 'Edit'}
        </EditButton>
        {editable && <SaveButton onClick={handleSave}>Save</SaveButton>}
      </FormFooter>
    </FormPanel>
  );
};

export default PFSedit;

const FormPanel = styled.div`
  width: 700px;
  max-height: 90vh;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  font-family: 'Segoe UI', sans-serif;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const FormHeader = styled.div`
  background-color: #2e7d32;
  color: white;
  padding: 5px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
`;

const Icons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DownloadWrapper = styled.div`
  position: relative;
`;

const DownloadBtn = styled.button`
  background: none;
  color: white;
  border: none;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    color: #ffdede;
  }
`;

const DropdownMenu = styled.ul`
  position: absolute;
  top: 32px;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  border-radius: 6px;
  list-style: none;
  padding: 5px 0;
  margin: 0;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 999;

  li {
    padding: 8px 16px;
    font-size: 14px;
    cursor: pointer;

    &:hover {
      background-color: #f3f3f3;
    }
  }
`;

const CloseBtn = styled.button`
  background: none;
  color: white;
  font-size: 20px;
  border: none;
  cursor: pointer;

  &:hover {
    color: #ffdede;
  }
`;

const FormBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex-grow: 1;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
`;

const Label = styled.label`
  font-size: 13px;
  margin-bottom: 4px;
  color: #555;
`;

const Input = styled.input`
  padding: 10px;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid #ccc;

  &:focus {
    outline: none;
    border-color: #4caf50;
    box-shadow: 0 0 4px rgba(76, 175, 80, 0.3);
  }
`;

const StaticValue = styled.p`
  padding: 10px;
  font-size: 14px;
  background-color: #f3f3f3;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
`;

const FormFooter = styled.div`
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  background-color: #f9f9f9;
  gap: 10px;
`;

const SaveButton = styled.button`
  padding: 10px 20px;
  background-color: #2e7d32;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #27672a;
  }
`;

const EditButton = styled.button`
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;

  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #005bb5;
  }
`;
